import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { List, Set } from 'immutable';

import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import CheckCircleIcon from '@mui/icons-material/CheckOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import Button from 'src/component/UI/Button';
import { getSingularWorkTicketLabel } from 'src/module/authentication/selector';
import { formatCircuitTag } from 'src/utils/utils';
import { formatAlarmLabel } from 'src/component/AlarmsList';
import { useTranslation } from 'src/i18n';

import './BulkValidateModal.scss';

export default function BulkValidateModal (props) {
  const { t } = useTranslation();
  const [notificationNumber, setNotificationNumber] = useState('');
  const [notificationDescription, setNotificationDescription] = useState('');
  const [expandedCircuits, setExpandedCircuits] = useState<Set<string>>(Set());

  const singularWorkTicketLabel = useSelector(getSingularWorkTicketLabel);

  // Reset state when modal closes
  useEffect(() => {
    if (!props.open) {
      setNotificationNumber('');
      setNotificationDescription('');
      setExpandedCircuits(Set());
    }
  }, [props.open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    props.handleSubmit(notificationNumber, notificationDescription);
    setNotificationNumber('');
    setNotificationDescription('');
  };

  const getCircuitLabel = (device: any) => {
    const circuitTag = device.get('circuit_tag');
    const controllerTag = device.get('controller_tag');
    if (circuitTag) {
      return formatCircuitTag(circuitTag, controllerTag);
    }
    return controllerTag || device.get('raw_tag') || 'Unknown Device';
  };

  const getTotalAlarmCount = () => {
    let count = 0;
    props.selectedDevices.forEach((device: any) => {
      count += device.get('alarms')?.size || 0;
    });
    return count;
  };

  const toggleCircuitExpanded = (deviceId: string) => {
    if (expandedCircuits.has(deviceId)) {
      setExpandedCircuits(expandedCircuits.delete(deviceId));
    } else {
      setExpandedCircuits(expandedCircuits.add(deviceId));
    }
  };

  return (
    <Modal open={props.open} onClose={props.handleClose} aria-labelledby='bulk-validate-modal-title'>
      <div className='modal bulk-validate-modal'>
        <h2 id='bulk-validate-modal-title'>{t('bulkValidate.validateMultipleCircuitAlarms')}</h2>

        <div className='selected-circuits-summary'>
          <div className='summary-item'>
            <span className='summary-label'>{t('bulkValidate.selectedCircuits')}</span>
            <span className='summary-value'>{props.selectedDevices.size}</span>
          </div>
          <div className='summary-item'>
            <span className='summary-label'>{t('bulkValidate.totalAlarms')}</span>
            <span className='summary-value'>{getTotalAlarmCount()}</span>
          </div>
        </div>

        <div className='circuits-list'>
          <h4>{t('bulkValidate.circuitsToValidate')}</h4>
          <div className='circuits-scroll'>
            {props.selectedDevices.map((device: any, index: number) => {
              const deviceId = device.get('device_id') || `device-${index}`;
              const alarms = device.get('alarms') || List();
              const alarmCount = alarms.size || 0;
              const isExpanded = expandedCircuits.has(deviceId);

              return (
                <div key={deviceId} className={`circuit-item-wrapper ${isExpanded ? 'expanded' : ''}`}>
                  <div
                    className='circuit-item'
                    onClick={() => alarmCount > 0 && toggleCircuitExpanded(deviceId)}
                    style={{ cursor: alarmCount > 0 ? 'pointer' : 'default' }}
                  >
                    <span className='circuit-tag'>{getCircuitLabel(device)}</span>
                    <div className='circuit-item-right'>
                      <span className='alarm-count'>
                        {alarmCount} alarm{alarmCount !== 1 ? 's' : ''}
                      </span>
                      {alarmCount > 0 && (
                        <IconButton
                          size='small'
                          className='expand-btn'
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCircuitExpanded(deviceId);
                          }}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </div>
                  </div>
                  <Collapse in={isExpanded}>
                    <div className='alarms-list'>
                      {alarms.map((alarm: any, alarmIndex: number) => (
                        <div key={alarm.get('id') || alarmIndex} className='alarm-item'>
                          {formatAlarmLabel(alarm.get('description') || alarm.get('alarm_group') || 'Unknown Alarm')}
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </div>
              );
            })}
          </div>
        </div>

        <form noValidate autoComplete='off' onSubmit={handleSubmit}>
          <div className='form-field'>
            <TextField
              value={notificationNumber}
              onChange={(e) => { setNotificationNumber(e.target.value); }}
              label={t('notificationModal.number', { label: singularWorkTicketLabel || t('workTickets.singular') })}
              placeholder={t('notificationModal.enterNumber', { label: singularWorkTicketLabel || t('workTickets.singular') })}
              variant='outlined'
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          </div>

          <div className='form-field'>
            <TextField
              value={notificationDescription}
              onChange={(e) => { setNotificationDescription(e.target.value); }}
              label={t('notificationModal.description', { label: singularWorkTicketLabel || t('workTickets.singular') })}
              placeholder={t('notificationModal.enterDescription')}
              variant='outlined'
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>

          <div className='button-bar'>
            <Button onClick={props.handleClose}>Cancel</Button>
            <Button
              type='submit'
              icon={<CheckCircleIcon style={{ fontSize: 14 }} />}
              cta
              disabled={!notificationNumber.trim()}
            >
              Validate All ({props.selectedDevices.size})
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

BulkValidateModal.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedDevices: ImmutablePropTypes.list
};

BulkValidateModal.defaultProps = {
  selectedDevices: List()
};
